import { Http } from '@mygpt/app/http';

export interface RealtimeBotItem {
  id: string;
  name: string | null;
  session_count: number;
  first_session_at: string | null;
  last_session_at: string | null;
}

export interface RealtimeUsageSession {
  session_id: string;
  created_at: string;
  title?: string | null;
  turn_count: number;
  duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  input_text_tokens: number;
  input_audio_tokens: number;
  cached_tokens: number;
  output_text_tokens: number;
  output_audio_tokens: number;
  user_identifier: string | null;
  language: string | null;
}

export interface RealtimeUsageResponse {
  bot_id: string;
  start_time: string;
  end_time: string;
  session_count: number;
  totals: Record<string, number>;
  sessions: RealtimeUsageSession[];
}

export interface RealtimeCostItem {
  category: string;
  tokens: number;
  unit_price: number;
  cost: number;
}

export interface RealtimeCostResponse {
  bot_id: string;
  start_time: string;
  end_time: string;
  model: string;
  session_count: number;
  total_turns: number;
  total_duration_ms: number;
  cache_hit_rate: number;
  items: RealtimeCostItem[];
  total_cost: number;
  totals: Record<string, number>;
}

export interface RealtimeCostDay {
  date: string;
  session_count: number;
  turn_count: number;
  duration_ms: number;
  total_tokens: number;
  input_text_tokens: number;
  input_audio_tokens: number;
  cached_tokens: number;
  output_text_tokens: number;
  output_audio_tokens: number;
  total_cost: number;
  cache_hit_rate: number;
}

export interface RealtimeCostDailyResponse {
  bot_id: string;
  start_time: string;
  end_time: string;
  model: string;
  tz_offset_minutes: number;
  days: RealtimeCostDay[];
}

export interface RealtimeSessionCost {
  session_id: string;
  created_at: string;
  bot_id: string;
  bot_name: string | null;
  provider: string;
  user_identifier: string | null;
  language: string | null;
  turn_count: number;
  duration_ms: number;
  total_tokens: number;
  input_text_tokens: number;
  input_audio_tokens: number;
  cached_tokens: number;
  output_text_tokens: number;
  output_audio_tokens: number;
  total_cost: number;
}

export interface RealtimeSessionsCostResponse {
  bot_id: string;
  start_time: string;
  end_time: string;
  model: string;
  session_count: number;
  total_cost: number;
  sessions: RealtimeSessionCost[];
}

export interface CostQueryParams {
  start_time?: string;
  end_time?: string;
  model?: string;
}

export interface DailyQueryParams extends CostQueryParams {
  tz_offset_minutes?: number;
}

export const listRealtimeBots = () => {
  const { get } = Http.use();
  return get<RealtimeBotItem[]>('/questions/realtime-cost/bots');
};

export const getRealtimeCost = (aiId: string, params: CostQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeCostResponse>(`/questions/${aiId}/realtime-cost`, { params });
};

export const getRealtimeCostSummary = (params: CostQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeCostResponse>('/questions/realtime-cost/summary', { params });
};

export const getRealtimeCostDaily = (aiId: string, params: DailyQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeCostDailyResponse>(`/questions/${aiId}/realtime-cost/daily`, { params });
};

export const getRealtimeCostSummaryDaily = (params: DailyQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeCostDailyResponse>('/questions/realtime-cost/summary/daily', { params });
};

export const getRealtimeUsage = (aiId: string, params: CostQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeUsageResponse>(`/questions/${aiId}/realtime-usage`, { params });
};

export const getRealtimeCostSessions = (aiId: string, params: CostQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeSessionsCostResponse>(`/questions/${aiId}/realtime-cost/sessions`, { params });
};

export const getRealtimeCostSummarySessions = (params: CostQueryParams) => {
  const { get } = Http.use();
  return get<RealtimeSessionsCostResponse>('/questions/realtime-cost/summary/sessions', { params });
};
